import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import MenuSidebar from "./MenuSidebar";
import { menu } from "./menuData";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "../../redux/reducer/auth/authReducer";
import CustomBreadcrumbs from "../breadcrumbs/CustomBreadcrumbs";
import moment from "moment";
import "moment/locale/id";
import RouteMenu from "./RouteMenu";
import { farmerMenu } from "./farmerMenuData";
import { leftBarActions } from "../../redux/reducer/leftbar/leftBarReducer";
// import { ToastContainer } from "react-toastify";
import { spvMenu } from "./spvMenuData";
import { driverMenu } from "./driverMenuData";
import { adminPuncakMenuData } from "./adminPuncakMenuData";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  backgroundColor: "#F2F2F2",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function AdminMain() {
  // Route
  const navigate = useNavigate();

  // Material UI
  const theme = useTheme();

  // Local State
  const open = useSelector((state) => state.leftBar.leftBarStatus);

  // Redux
  const dispatch = useDispatch();
  const role = useSelector((state) => state.auth.dataToken.roleId.roleName);

  const handleDrawerOpen = () => {
    dispatch(leftBarActions.setLeftBar(true));
  };

  const handleDrawerClose = () => {
    dispatch(leftBarActions.setLeftBar(false));
  };

  const handleLogout = () => {
    dispatch(authActions.logout());
    navigate("/");
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* <ToastContainer /> */}
      <AppBar position="fixed" open={open} sx={{ height: "50px" }}>
        <Toolbar
          style={{
            minHeight: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: "none" }),
                color: "#223831",
              }}
            >
              <MenuIcon />
            </IconButton>
            <CustomBreadcrumbs />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography color={"#0A543B"} marginRight={1}>
              {moment().locale("id").format("dddd, DD-MM-YYYY")}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => handleLogout()}
              style={{ color: "#223831" }}
            >
              Logout
            </Button>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        classes={{ paper: { background: "black" } }}
        className="sidebar-menu"
      >
        <DrawerHeader
          style={{
            height: "50px",
            minHeight: "50px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" noWrap marginLeft={1}>
            SFO-FARMS
          </Typography>
          <IconButton onClick={handleDrawerClose} style={{ color: "#b0b0b0" }}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <div>
          {role === "super-admin"
            ? menu
                .sort((a, b) =>
                  a.title > b.title ? 1 : b.title > a.title ? -1 : 0
                )
                .map((item, key) => <MenuSidebar key={key} item={item} />)
            : role === "supervisor"
            ? spvMenu
                .sort((a, b) =>
                  a.title > b.title ? 1 : b.title > a.title ? -1 : 0
                )
                .map((item, key) => <MenuSidebar key={key} item={item} />)
            : role === "farmer"
            ? farmerMenu
                .sort((a, b) =>
                  a.title > b.title ? 1 : b.title > a.title ? -1 : 0
                )
                .map((item, key) => <MenuSidebar key={key} item={item} />)
            : role === "driver"
            ? driverMenu
                .sort((a, b) =>
                  a.title > b.title ? 1 : b.title > a.title ? -1 : 0
                )
                .map((item, key) => <MenuSidebar key={key} item={item} />)
            : role === "admin"
            ? adminPuncakMenuData.map((item, key) => (
                <MenuSidebar key={key} item={item} />
              ))
            : ""}
        </div>
      </Drawer>
      <Box
        component="main"
        sx={{ width: open ? "80vw" : "90vw", height: "85vh" }}
      >
        <RouteMenu />
      </Box>
    </Box>
  );
}
